/**
 * One open rundown in the editor: load it, keep it live, write to it — the
 * non-React half of the rundown editor (PLAN §6.2, §6.5, §10.2).
 *
 *   load       `GET /rundowns/{id}` + every page of `…/rows` into the row store
 *   live       the WS watch client from the listing's cursor: `row.state`
 *              into the store, a reconnect resynchronised by its
 *              `changedSince` catch-up, presence in and out
 *   write      `commit` applies patches optimistically (`rowStore.ts`) and
 *              queues them; the queue is posted as `POST …/patches` batches,
 *              ONE request in flight at a time so a creation always reaches
 *              the server before the edits of the row it creates; each
 *              result acks (canonical row adopted) or nacks (rolled back,
 *              reported)
 *   undo/redo  the per-user stacks of `undoStack.ts`
 *   primary    the connectivity manager (PLAN §10.2) hears the socket's state
 *              (`reportSocket`); when the primary backend changes (a
 *              failover, the operator's switch) the view calls `setPrimary`
 *              and the session reloads from the new primary and resubscribes
 *              there — the WS URL is read at every subscribe, and a cursor is
 *              opaque to the host that issued it, so it is never carried over
 *
 * **Writes go over REST, not the socket.** Both are the contract (PLAN §6.5);
 * REST answers every patch in its own response, which is what reconciling and
 * rolling back need, and the node and the cloud fan the result out as
 * `row.state` to every subscriber either way. A request that fails outright
 * (offline, 5xx) rolls its patches back and says so: an offline edit queue is
 * the multi-connection manager's (Wave 2b-2), not this module's.
 *
 * Every dependency is a port (CLAUDE.md → explicit dependency injection); the
 * app passes the browser's (`app/browserPorts.ts`), tests the fakes of
 * `@acp/api-client/testing/fakes.js`.
 */
import type { ContractParsing } from '@acp/api-contract/parsing.js';
import type { ApiClient } from '@acp/api-client/client.js';
import type { SocketFactory, Timers } from '@acp/api-client/ports.js';
import { listAllRows } from '@acp/api-client/rows.js';
import { watchRundown, type WatchEvent, type WatchHandle } from '@acp/api-client/watchClient.js';
import { MAX_PATCHES_PER_BATCH, type PatchResult } from '@acp/api-contract/rows.js';
import type { PresenceMember, PresenceState } from '@acp/api-contract/ws.js';
import type { SocketState } from '../connectivity/state.js';
import type { Row, RowPatch } from '@acp/domain/row.js';
import type { RowTree } from '@acp/domain/tree.js';
import { createRowStore, type RowStore } from '../lib/editor/rowStore.js';
import { EMPTY_STACKS, planStep, pushEdit, type UndoStacks } from '../lib/editor/undoStack.js';
import { createWriter, type Writer } from '../lib/editor/writer.js';

/** How long typing may accumulate before the queue is posted (one request per burst, not per key). */
export const FLUSH_DELAY_MS = 40;

export type EditorConnection = 'connecting' | 'live' | 'reconnecting';

export type EditorSnapshot = {
  readonly status: 'loading' | 'ready' | 'failed';
  readonly error: string | null;
  readonly connection: EditorConnection;
  /** When the next reconnect attempt is due (ms from the disconnect), while reconnecting. */
  readonly retryInMs: number | null;
  /**
   * The materialised tree, as a new object whenever it changed (the tree
   * itself is mutated in place by the row store), so a view can memoise on it.
   */
  readonly rows: { readonly tree: RowTree; readonly version: number };
  readonly members: readonly PresenceMember[];
  readonly canUndo: boolean;
  readonly canRedo: boolean;
  /** Patches applied locally and not yet answered. */
  readonly pending: number;
};

export type EditorNotice = { readonly level: 'error' | 'info'; readonly message: string };

export type EditorSessionDeps = {
  readonly api: ApiClient;
  /** The primary's WS URL, read at every subscribe (it changes on a failover). */
  readonly wsUrl: () => string;
  /** The primary's socket state, for the connectivity panel. */
  readonly reportSocket: (state: SocketState) => void;
  readonly tenantId: string;
  readonly rundownId: string;
  readonly token: () => Promise<string>;
  readonly openSocket: SocketFactory;
  readonly timers: Timers;
  readonly nowMs: () => number;
  /** How WS frames are read (D35): the services' mode. */
  readonly parsing: ContractParsing;
  readonly randomBytes: (count: number) => Uint8Array;
  /** Something the user should be told (a refused edit, an undo that had nothing left). */
  readonly notify: (notice: EditorNotice) => void;
};

export type EditorSession = {
  readonly store: RowStore;
  readonly writer: Writer;
  readonly snapshot: () => EditorSnapshot;
  readonly subscribe: (listener: () => void) => () => void;
  /** Apply and send patches; `undoable` records them on the undo stack. */
  readonly commit: (patches: readonly RowPatch[], undoable: boolean) => void;
  readonly undo: () => void;
  readonly redo: () => void;
  readonly setPresence: (state: PresenceState) => void;
  /** Load again after a failed load. */
  readonly retry: () => void;
  /**
   * Load and go live. Idempotent while started; after `stop()` it starts over
   * (React's StrictMode mounts, unmounts and mounts again).
   */
  readonly start: () => void;
  readonly stop: () => void;
  /**
   * The primary backend's id. The first call records it; a different one
   * later reloads everything from the new primary and resubscribes there.
   */
  readonly setPrimary: (primaryId: string) => void;
};

function messageOf(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

function nackMessage(result: Extract<PatchResult, { status: 'nack' }>): string {
  return result.rejected.map((r) => r.message).join('; ');
}

export function createRundownEditorSession(deps: EditorSessionDeps): EditorSession {
  const store = createRowStore();
  const writer = createWriter(deps.nowMs, deps.randomBytes);
  const listeners = new Set<() => void>();
  let snap: EditorSnapshot = {
    status: 'loading',
    error: null,
    connection: 'connecting',
    retryInMs: null,
    rows: { tree: store.tree, version: store.version() },
    members: [],
    canUndo: false,
    canRedo: false,
    pending: 0,
  };
  let stacks: UndoStacks = EMPTY_STACKS;
  let presence: PresenceState = { rowId: null, field: null, activity: 'viewing' };
  let watch: WatchHandle | null = null;
  let stopped = true;
  /**
   * Bumped by every start, stop and primary change: a load or a watch from an
   * older generation drops its result (StrictMode's start → stop → start ran
   * two loads, and the first one's socket leaked — found in review).
   */
  let generation = 0;
  let primaryId: string | null = null;
  /**
   * Bumped when the primary changes. A patch batch remembers the epoch it was
   * sent in; an answer from an older epoch comes from the OLD host, whose row
   * versions mean nothing next to the new host's, so it is ignored and the
   * batch is sent again to the new primary (same ids and HLCs: idempotent).
   * Second review: such an answer could pin a row at a stale higher version.
   */
  let primaryEpoch = 0;
  /**
   * A fresh (version-blind) load from the current primary is owed until one
   * succeeds: set by a primary change, kept through a failed load, so Retry
   * and a restart load fresh too (second review: Retry compared the new
   * host's versions with the old one's and kept the old data).
   */
  let freshOwed = false;
  const queue: RowPatch[] = [];
  /** clientOpIds of unanswered patches that carry a Yjs update. */
  const carriesText = new Set<string>();
  const enqueue = (patches: readonly RowPatch[]): void => {
    for (const p of patches) if (Object.keys(p.text ?? {}).length > 0) carriesText.add(p.clientOpId);
    queue.push(...patches);
  };
  let inFlight = false;
  let cancelFlush: (() => void) | null = null;

  const update = (patch: Partial<EditorSnapshot>): void => {
    const rows = snap.rows.version === store.version() ? snap.rows : { tree: store.tree, version: store.version() };
    snap = { ...snap, ...patch, rows, canUndo: stacks.undo.length > 0, canRedo: stacks.redo.length > 0, pending: store.pendingCount() };
    for (const listener of listeners) listener();
  };

  const receive = (rows: readonly Row[]): void => {
    for (const row of rows) writer.observe(row);
    store.receive(rows, false);
  };

  const report = (notice: EditorNotice): void => {
    if (!stopped) deps.notify(notice);
  };

  // Results are settled even after `stop()`: the store must not keep a patch pending forever.
  const settle = (results: readonly PatchResult[]): void => {
    for (const result of results) {
      if (result.status === 'ack') {
        writer.observe(result.row);
        store.ack(result.clientOpId, result.row);
      } else {
        store.nack(result.clientOpId, result.row);
        if (carriesText.has(result.clientOpId)) writer.rotateTextClient();
        report({ level: 'error', message: `Edit refused: ${nackMessage(result)}` });
      }
      carriesText.delete(result.clientOpId);
    }
  };

  /** A batch whose request failed without an answer: roll it back and say so. */
  const rollBack = (batch: readonly RowPatch[], err: unknown): void => {
    for (const patch of batch) store.nack(patch.clientOpId, null);
    // The server may have applied them: never reuse their Yjs numbering (writer.ts). Every marker goes, not just the first.
    const hadText = batch.map((patch) => carriesText.delete(patch.clientOpId)).includes(true);
    if (hadText) writer.rotateTextClient();
    report({ level: 'error', message: `Not saved: ${messageOf(err)}` });
  };

  const flush = async (): Promise<void> => {
    cancelFlush = null;
    // While a fresh load is owed, `confirmed` still holds the former primary's versions, and an
    // ack from the new one could lose to them: the queue waits for the fresh listing (review).
    if (inFlight || stopped || freshOwed || queue.length === 0) return;
    const batch = queue.splice(0, MAX_PATCHES_PER_BATCH);
    const epoch = primaryEpoch;
    inFlight = true;
    try {
      const res = await deps.api.call('patchRows', { params: { rundownId: deps.rundownId }, query: null, body: { patches: batch } });
      if (epoch === primaryEpoch) settle(res.results);
      else resend(batch);
    } catch (err) {
      if (epoch === primaryEpoch) rollBack(batch, err);
      else resend(batch);
    } finally {
      inFlight = false;
    }
    update({});
    if (queue.length > 0) void flush();
  };

  /** A batch answered by (or lost to) a former primary: still pending, sent again first. */
  const resend = (batch: readonly RowPatch[]): void => {
    queue.unshift(...batch.filter((p) => store.isPending(p.clientOpId)));
  };

  const scheduleFlush = (): void => {
    if (cancelFlush !== null || stopped) return;
    cancelFlush = deps.timers.after(FLUSH_DELAY_MS, () => {
      void flush();
    });
  };

  const onEvent = (event: WatchEvent): void => {
    if (stopped) return;
    switch (event.type) {
      case 'subscribed':
        deps.reportSocket('open');
        update({ connection: 'live', retryInMs: null });
        return;
      case 'row':
        if (event.clientOpId !== null && store.isPending(event.clientOpId)) {
          writer.observe(event.row);
          store.ack(event.clientOpId, event.row);
        } else {
          receive([event.row]);
        }
        update({});
        return;
      case 'presence':
        update({ members: event.members });
        return;
      case 'presence-leave':
        update({ members: snap.members.filter((m) => m.connectionId !== event.connectionId) });
        return;
      case 'disconnected':
        deps.reportSocket('connecting');
        update({ connection: 'reconnecting', retryInMs: event.retryInMs });
        return;
      case 'nack':
        deps.notify({ level: 'error', message: `The server refused: ${event.message.payload.message}` });
        return;
      case 'warning':
        deps.notify({ level: 'info', message: event.message });
        return;
      case 'patch':
        // Another writer's batch; its canonical rows follow as row.state.
        return;
    }
  };

  const startWatch = (cursor: string | null, gen: number): void => {
    watch?.stop();
    deps.reportSocket('connecting');
    const handle = watchRundown({
      wsUrl: deps.wsUrl(),
      rundownId: deps.rundownId,
      tenantId: deps.tenantId,
      token: deps.token,
      openSocket: deps.openSocket,
      timers: deps.timers,
      nowMs: deps.nowMs,
      parsing: deps.parsing,
      catchUp: (from) => listAllRows(deps.api, deps.rundownId, { changedSince: from }),
      since: cursor,
      presence,
      onEvent: (event) => {
        if (gen === generation) onEvent(event);
      },
    });
    watch = handle;
    handle.done.catch((err: unknown) => {
      if (stopped || gen !== generation) return;
      deps.reportSocket('closed');
      update({ connection: 'reconnecting', error: `live updates stopped: ${messageOf(err)}` });
    });
  };

  const adoptListing = (rows: readonly Row[], fresh: boolean): void => {
    for (const row of rows) writer.observe(row);
    store.receive(rows, fresh);
    if (fresh) freshOwed = false;
    update({ status: 'ready', error: null });
    if (fresh && queue.length > 0) scheduleFlush();
  };

  /** `fresh`: from another host, whose row versions are its own — adopt its rows whatever our versions say. */
  const load = async (fresh: boolean): Promise<void> => {
    const gen = generation;
    // A switch keeps the rows on screen while the new primary's arrive; anything else shows it is loading
    // (and a Retry, no longer `failed`, cannot start a second load).
    if (!fresh || snap.status !== 'ready') update({ status: 'loading', error: null });
    try {
      const head = await deps.api.call('getRundown', { params: { rundownId: deps.rundownId }, query: null, body: null });
      const listing = await listAllRows(deps.api, deps.rundownId, {});
      if (stopped || gen !== generation) return;
      adoptListing([head.rundown, ...listing.rows], fresh);
      startWatch(listing.cursor ?? head.cursor, gen);
    } catch (err) {
      if (!stopped && gen === generation) update({ status: 'failed', error: messageOf(err) });
    }
  };

  const step = (direction: 'undo' | 'redo'): void => {
    const plan = planStep(
      stacks,
      direction,
      (rowId) => store.get(rowId),
      () => ({ hlc: writer.clock.now(), origin: writer.nodeId, clientOpId: writer.newOpId(), textClientId: writer.textClientId() }),
      deps.nowMs(),
    );
    stacks = plan.stacks;
    if (!plan.ok) {
      if (plan.reason !== 'empty') deps.notify({ level: 'info', message: plan.message });
      update({});
      return;
    }
    store.applyLocal(plan.patches);
    enqueue(plan.patches);
    scheduleFlush();
    if (plan.overwritten.length > 0) deps.notify({ level: 'info', message: `Left alone (changed by someone since): ${plan.overwritten.join(', ')}` });
    update({});
  };

  return {
    store,
    writer,
    snapshot: () => snap,
    subscribe: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    commit: (patches, undoable) => {
      if (patches.length === 0 || stopped) return;
      const before = store.applyLocal(patches);
      if (undoable) stacks = pushEdit(stacks, { items: patches.map((patch, i) => ({ rowBefore: before[i] ?? null, patch })), atMs: deps.nowMs() });
      enqueue(patches);
      scheduleFlush();
      update({});
    },
    undo: () => step('undo'),
    redo: () => step('redo'),
    setPresence: (state) => {
      presence = state;
      watch?.setPresence(state);
    },
    retry: () => {
      if (snap.status === 'failed') void load(freshOwed);
    },
    start: () => {
      if (!stopped) return;
      stopped = false;
      generation += 1;
      void load(freshOwed);
      if (queue.length > 0) scheduleFlush();
    },
    stop: () => {
      if (stopped) return;
      stopped = true;
      generation += 1;
      cancelFlush?.();
      cancelFlush = null;
      watch?.stop();
      watch = null;
      deps.reportSocket('unknown');
      update({ connection: 'connecting' });
    },
    setPrimary: (id) => {
      const changed = primaryId !== null && primaryId !== id;
      primaryId = id;
      if (!changed) return;
      primaryEpoch += 1;
      freshOwed = true;
      if (stopped) return;
      generation += 1;
      watch?.stop();
      watch = null;
      update({ connection: 'connecting', retryInMs: null });
      void load(true);
      if (queue.length > 0) scheduleFlush();
    },
  };
}
